// src/dashboard/Properties.jsx
import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react';
import { useIntl } from 'react-intl';
import { db } from '../config/firebaseConfig';
import {
  collection, getDocs, query, orderBy, limit, startAfter, getCountFromServer,
  where, writeBatch, doc, serverTimestamp
} from 'firebase/firestore';
import { AuthContext } from '../context/AuthContext';
import { toast } from 'react-toastify';
import CSVUpload from '../components/CSVUpload';
import PropertiesList from '../components/PropertiesList';

export default function Properties() {
  const intl = useIntl();
  const { user } = useContext(AuthContext);
  const PAGE_SIZE = 6;

  const [dataVersion, setDataVersion] = useState(0);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const [totalProperties, setTotalProperties] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageCursors, setPageCursors] = useState([null]);
  const [isLastPage, setIsLastPage] = useState(false);

  const [propertiesToSave, setPropertiesToSave] = useState([]);
  const [isSaving, setIsSaving] = useState(false);

  const handleDataChange = () => {
    setCurrentPage(1);
    setPageCursors([null]);
    setIsLastPage(false);
    setDataVersion(prev => prev + 1);
  };

  function useDebounce(value, delay) {
    const [debouncedValue, setDebouncedValue] = useState(value);
    useEffect(() => {
      const handler = setTimeout(() => setDebouncedValue(value), delay);
      return () => clearTimeout(handler);
    }, [value, delay]);
    return debouncedValue;
  }

  const debouncedSearchTerm = useDebounce(searchTerm, 500);

  useEffect(() => {
    const getTotalCount = async () => {
      if (!user?.uid) return;
      try {
        const propertiesRef = collection(db, `users/${user.uid}/properties`);
        const countSnapshot = await getCountFromServer(propertiesRef);
        setTotalProperties(countSnapshot.data().count);
      } catch (err) {
        console.error("Error fetching total property count:", err);
      }
    };
    getTotalCount();
  }, [user, dataVersion]);

  const fetchProperties = useCallback(async () => {
    if (!user?.uid) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);

    try {
      const propertiesRef = collection(db, `users/${user.uid}/properties`);
      let q;

      if (debouncedSearchTerm) {
        q = query(propertiesRef, where('searchable_terms', 'array-contains', debouncedSearchTerm.toLowerCase()), limit(20));
      } else {
        const constraints = [orderBy('updatedAt', 'desc'), limit(PAGE_SIZE)];
        const cursor = pageCursors[currentPage - 1];
        if (cursor) constraints.push(startAfter(cursor));
        q = query(propertiesRef, ...constraints);
      }

      const querySnapshot = await getDocs(q);
      const propsList = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
      setProperties(propsList);

      if (!debouncedSearchTerm) {
        setIsLastPage(querySnapshot.docs.length < PAGE_SIZE);
        const lastVisibleDoc = querySnapshot.docs[querySnapshot.docs.length - 1];
        if (lastVisibleDoc && pageCursors.length === currentPage) {
          setPageCursors(prev => [...prev, lastVisibleDoc]);
        }
      }
    } catch (err) {
      console.error("Erro ao buscar imóveis:", err);
      setError(intl.formatMessage({ id: 'csvUpload.networkError' }));
    } finally {
      setLoading(false);
    }
  }, [user, dataVersion, currentPage, debouncedSearchTerm, intl]);

  useEffect(() => {
    fetchProperties();
  }, [fetchProperties]);

  const handleProcessSuccess = (processedProperties) => {
    setPropertiesToSave(processedProperties || []);
    if (processedProperties?.length > 0) {
      setTimeout(() => {
        document.getElementById('preview-section')?.scrollIntoView({ behavior: 'smooth' });
      }, 100);
    }
  };

  const handleSaveProperties = async () => {
    if (!user || propertiesToSave.length === 0) return;
    setIsSaving(true);
    setError(null);

    const batch = writeBatch(db);
    propertiesToSave.forEach((property) => {
      const propertyRef = doc(db, `users/${user.uid}/properties`, property.id);
      const { id, ...dataToSave } = property;
      batch.set(propertyRef, {
        ...dataToSave,
        createdAt: serverTimestamp(),
        updatedAt: serverTimestamp(),
      });
    });

    try {
      await batch.commit();
      toast.success(intl.formatMessage({ id: 'csvUpload.saveSuccessMultiple' }, { count: propertiesToSave.length }));
      setPropertiesToSave([]);
      handleDataChange();
    } catch (err) {
      console.error("Erro ao salvar imóveis:", err);
      setError(intl.formatMessage({ id: 'csvUpload.saveError' }));
      toast.error(intl.formatMessage({ id: 'csvUpload.saveError' }));
    } finally {
      setIsSaving(false);
    }
  };

  const handleNextPage = () => {
    if (!isLastPage) setCurrentPage(prev => prev + 1);
  };

  const handlePrevPage = () => {
    if (currentPage > 1) setCurrentPage(prev => prev - 1);
  };

  return (
    <div className="la">
      <div className="dashboard-content">
        <h1 className="page-title">
          {intl.formatMessage({ id: 'properties.title', defaultMessage: 'Gerenciamento de Imóveis' })}
        </h1>
        <p className="subtitle-muted">
          {intl.formatMessage({ id: 'properties.subtitle', defaultMessage: 'Importe, visualize e gerencie seus imóveis.' })}
        </p>

        {/* Lista de Imóveis */}
        <section className="card mb-16">
          <div className="section-title">
            {intl.formatMessage({ id: 'properties.registered', defaultMessage: 'Imóveis Cadastrados' })}
          </div>

          <div className="form-group full-width mt-16">
            <input
              type="text"
              className="input-field"
              placeholder={intl.formatMessage({ id: 'properties.searchPlaceholder' })}
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>

          <PropertiesList properties={properties} loading={loading} error={error} />

          {!debouncedSearchTerm && totalProperties > 0 && (
            <div className="pagination-controls mt-16">
              <button onClick={handlePrevPage} disabled={currentPage === 1 || loading} className="secondary-button">
                &larr; {intl.formatMessage({ id: 'pagination.previous' })}
              </button>
              <span className="muted">{intl.formatMessage({ id: 'pagination.page' })} {currentPage}</span>
              <button onClick={handleNextPage} disabled={isLastPage || loading} className="secondary-button">
                {intl.formatMessage({ id: 'pagination.next' })} &rarr;
              </button>
            </div>
          )}
        </section>

        {/* Upload de CSV */}
        <section className="card mb-16">
          <CSVUpload
            onProcessSuccess={handleProcessSuccess}
            onUploadSuccess={handleDataChange}
            hasExistingProperties={totalProperties > 0}
          />
        </section>

        {/* Pré-visualização */}
        {propertiesToSave.length > 0 && (
          <section id="preview-section" className="card">
            <div className="section-title">
              {intl.formatMessage({ id: 'csvUpload.previewTitle' })}
            </div>
            <p className="subtitle-muted">
              {intl.formatMessage({ id: 'csvUpload.previewSubtitle' })}
            </p>

            <div className="mt-16">
              <PropertiesList properties={propertiesToSave} loading={isSaving} />
            </div>

            <div className="mt-16">
              <button className="primary-button" onClick={handleSaveProperties} disabled={isSaving}>
                {isSaving
                  ? intl.formatMessage({ id: 'csvUpload.saving' })
                  : intl.formatMessage({ id: 'csvUpload.saveButton' })}
              </button>
            </div>
          </section>
        )}
      </div>
    </div>
  );
}
